import { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
// Wir nutzen GridLayout (default-Export) ohne Responsive-Wrapper —
// Desktop-only (Joerg 2026-05-26), keine Mobile-Breakpoints. ResizeObserver
// in einem eigenen useEffect liefert width an GridLayout.
// Type-Cast: @types/react-grid-layout v1.3 deckt nicht alle Props ab.
import GridLayoutImport from 'react-grid-layout';
const GridLayout = GridLayoutImport as unknown as React.ComponentType<Record<string, unknown> & { children?: React.ReactNode }>;
import { Loader2, Plus, X, Pencil, SeparatorHorizontal, GripVertical } from 'lucide-react';
import { authFetch, useAuth } from '@onvice/portal-shell-api';

import ModuleErrorBoundary from './ModuleErrorBoundary';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

interface LayoutItem { i: string; x: number; y: number; w: number; h: number; minW?: number; minH?: number; config?: Record<string, unknown>; }
type Layout = LayoutItem[];

interface AvailableWidget {
  slug: string;
  label: string;
  module: string;
  description: string;
  default_w: number;
  default_h: number;
  min_w?: number;
  min_h?: number;
}

/**
 * Generischer Widget-Grid-Wrapper fuer die /uebersicht/*-Sichten
 * (Joerg 2026-05-26). Laedt das Layout von /portal/page-layout/:slug,
 * rendert die Widgets aus einer Map (slug → JSX), und persistiert
 * Aenderungen via PUT /portal/page-layout/:slug.
 *
 * Konzept: 04 Ressourcen/Softwarearchitektur/Widget-Picker-Konzept.md
 */

export interface WidgetGridProps {
  /** Legacy-Modus (Phase 0+1): page_slug -> /portal/page-layout/:slug. */
  pageSlug?: string;
  /** Neuer Modus (Phase 2a): persoenliche Seite -> /portal/my-pages/:id. */
  pageId?: string;
  /** Map slug → Renderfunktion. Slug muss zu Layout-i passen.
   *  Optionaler config-Parameter kommt aus layout_json[i].config (z.B.
   *  vom Klon-Operator: { mode: 'snapshot', filter: {...} }). */
  widgetMap: Record<string, (config?: Record<string, unknown>) => ReactNode>;
  /** Edit-Modus: drag + resize aktiv. */
  editing: boolean;
  /** Speichern-Trigger vom Parent (z.B. „Speichern"-Button im Header). */
  saveTick?: number;
  /** Callback nach erfolgreichem Speichern (z.B. setEditing(false)). */
  onSaved?: () => void;
  /** Callback wenn der User „Abbrechen" drueckt — Parent verwirft Layout-Aenderungen. */
  cancelTick?: number;
  /** Optional (nur pageSlug-Modus): Tenant-Default speichern statt User-Override. */
  saveAsTenantDefault?: boolean;
  /** Optional (nur pageId-Modus): Initial-Layout vom Parent durchgereicht (Antwort von /my-pages/:id). */
  initialLayout?: Layout;
  /** Optional (nur pageId-Modus): Wenn gesetzt, wird im Auftrag dieses anderen
   *  Users gespeichert — Save geht gegen /admin/users/:actAsUserId/pages/:id
   *  statt /my-pages/:id. Nur fuer Admin-UI „Seiten eines Users verwalten".
   *  Joerg-Wunsch 2026-06-05. */
  actAsUserId?: string;
  /** Template-Editor-Modus (Joerg 2026-06-08): wenn gesetzt, speichert das
   *  Save in eine Seiten-Vorlage statt in eine User-Page — PATCH gegen
   *  /admin/page-templates/:id mit { layout: ... }. Verdraengt pageId/pageSlug. */
  templateId?: string;
}

export default function WidgetGrid({ pageSlug, pageId, widgetMap, editing, saveTick, onSaved, cancelTick, saveAsTenantDefault, initialLayout, actAsUserId, templateId }: WidgetGridProps) {
  const { isAdmin } = useAuth();
  const [layout, setLayout] = useState<Layout | null>(initialLayout ?? null);
  const [originalLayout, setOriginalLayout] = useState<Layout | null>(initialLayout ?? null);
  const [source, setSource] = useState<'user' | 'tenant' | 'fallback' | 'page'>('fallback');
  const [loading, setLoading] = useState(!initialLayout && !!pageSlug);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(1200);
  // Widget-Picker (Phase 2d-Folgearbeit 2026-05-27): im Edit-Mode darf der
  // User Widgets hinzufuegen + entfernen. Liste kommt permission-gefiltert
  // vom Backend (acl.module_access).
  const [availableWidgets, setAvailableWidgets] = useState<AvailableWidget[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Container-Breite via ResizeObserver — GridLayout braucht expliziten width-Prop
  const containerRef = (el: HTMLDivElement | null) => {
    if (!el) return;
    const update = () => setWidth(el.clientWidth || 1200);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
  };

  // Layout vom Server laden (nur Legacy pageSlug-Modus; pageId-Modus kriegt initialLayout vom Parent)
  const loadLayout = useCallback(async () => {
    if (!pageSlug) return;
    setLoading(true);
    try {
      const res = await authFetch(`/api/portal/page-layout/${pageSlug}`);
      const j = await res.json();
      if (j.success) {
        const l = (j.data.layout ?? []) as Layout;
        setLayout(l);
        setOriginalLayout(l);
        setSource(j.data.source);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [pageSlug]);

  useEffect(() => { loadLayout(); }, [loadLayout]);

  // initialLayout-Aenderungen vom Parent (z.B. Page-Wechsel) reinziehen
  useEffect(() => {
    if (initialLayout) {
      setLayout(initialLayout);
      setOriginalLayout(initialLayout);
      setSource('page');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLayout]);

  // Speichern bei saveTick-Aenderung
  useEffect(() => {
    if (!saveTick || !layout) return;
    let cancelled = false;
    (async () => {
      setSaving(true);
      setError(null);
      try {
        let res: Response;
        if (templateId) {
          // Template-Editor: Layout in portal_page_templates schreiben.
          res = await authFetch(`/api/portal/admin/page-templates/${templateId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ layout }),
          });
        } else if (pageId) {
          // Neuer Modus: persoenliche Seite via PATCH. Mit actAsUserId
          // schreibt der Admin in die Pages eines anderen Users.
          const url = actAsUserId
            ? `/api/portal/admin/users/${actAsUserId}/pages/${pageId}`
            : `/api/portal/my-pages/${pageId}`;
          res = await authFetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ layout }),
          });
        } else if (pageSlug) {
          // Legacy: tenant_default oder user_override
          res = await authFetch(`/api/portal/page-layout/${pageSlug}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scope: saveAsTenantDefault ? 'tenant_default' : 'user_override',
              layout,
            }),
          });
        } else {
          throw new Error('WidgetGrid: weder pageId noch pageSlug gesetzt');
        }
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          setError(j.error ?? `HTTP ${res.status}`);
        } else if (!cancelled) {
          setOriginalLayout(layout);
          onSaved?.();
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setSaving(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTick]);

  // Abbrechen: Layout auf Original-Stand zuruecksetzen
  useEffect(() => {
    if (cancelTick && originalLayout) setLayout(originalLayout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelTick]);

  // Verfuegbare Widgets erst beim Edit-Mode-Eintritt laden — permission-
  // gefiltert. Nur im pageId/templateId-Modus laden, der Legacy-pageSlug-Pfad ist read-only.
  useEffect(() => {
    if (!editing || (!pageId && !templateId)) return;
    let cancelled = false;
    authFetch('/api/portal/widgets')
      .then((r) => r.json())
      .then((j: { success: boolean; data: AvailableWidget[] }) => {
        if (!cancelled && j.success) setAvailableWidgets(j.data);
      })
      .catch(() => { /* fail-soft */ });
    return () => { cancelled = true; };
  }, [editing, pageId]);

  // Beim Verlassen des Edit-Modes Picker schliessen
  useEffect(() => {
    if (!editing) setPickerOpen(false);
  }, [editing]);

  const addWidget = useCallback((w: AvailableWidget) => {
    setLayout((prev) => {
      if (!prev) return prev;
      if (prev.some((l) => l.i === w.slug)) return prev;
      const maxY = prev.reduce((m, l) => Math.max(m, l.y + l.h), 0);
      return [...prev, {
        i: w.slug, x: 0, y: maxY, w: w.default_w, h: w.default_h,
        ...(w.min_w ? { minW: w.min_w } : {}),
        ...(w.min_h ? { minH: w.min_h } : {}),
      }];
    });
  }, []);

  const removeWidget = useCallback((slug: string) => {
    setLayout((prev) => prev ? prev.filter((l) => l.i !== slug) : prev);
  }, []);

  // Run-3 #7 (Kiara 2026-05-28): eigene Section-Header anlegen. Slug ist
  // `custom.section.<random>` damit mehrere im selben Layout sein koennen;
  // `config.label` traegt den User-Text. Joerg 2026-05-28: oben einfuegen,
  // damit der User sofort sieht dass die Schaltflaeche gewirkt hat —
  // existierende Items werden um 1 nach unten verschoben. Plus Smooth-Scroll
  // zum Seitenanfang damit das neue Item garantiert im Viewport landet.
  const addCustomSection = useCallback(() => {
    setLayout((prev) => {
      if (!prev) return prev;
      const slug = `custom.section.${Math.random().toString(36).slice(2, 9)}`;
      const shifted = prev.map((l) => ({ ...l, y: l.y + 1 }));
      return [{ i: slug, x: 0, y: 0, w: 12, h: 1, config: { label: 'Neue Sektion' } }, ...shifted];
    });
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  }, []);

  const renameCustomSection = useCallback((slug: string, current: string) => {
    const next = window.prompt('Neue Ueberschrift:', current);
    if (next == null || next === current) return;
    setLayout((prev) => prev ? prev.map((l) => l.i === slug ? { ...l, config: { ...(l.config ?? {}), label: next } } : l) : prev);
  }, []);

  // Hooks muessen VOR den early-returns laufen, sonst kippt die Hook-Order
  // zwischen Renders (React #310). Daher activeSlugSet/widgetsByModule hier,
  // mit defensiven Defaults wenn layout noch null ist.
  const activeSlugSet = useMemo(() => new Set((layout ?? []).map((l) => l.i)), [layout]);
  const widgetsByModule = useMemo(() => {
    const map = new Map<string, AvailableWidget[]>();
    for (const w of availableWidgets) {
      const list = map.get(w.module) ?? [];
      list.push(w);
      map.set(w.module, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [availableWidgets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!layout) {
    return <div className="text-sm text-muted-foreground">Layout konnte nicht geladen werden.</div>;
  }

  // Widgets aus dem Layout filtern, die in der Map vorhanden sind (Permission-
  // gefilterte Slugs koennen fehlen — dann wird die Box leer gelassen).
  // Ausnahme: `custom.section.*`-Items werden inline gerendert (eigene Ueberschrift
  // aus l.config.label), nicht via widgetMap.
  // Im Nicht-Edit-Modus wird zusaetzlich `static:true` per Item gesetzt — das ist
  // react-grid-layouts harter Sperr-Mechanismus. Joerg-Befund 2026-06-05:
  // `isDraggable={editing}` allein reichte nicht; nach HTML5-Drop einer Plantafel-
  // Aufgabe startete react-grid-layout den Widget-Drag aufgrund des verschluckten
  // mouseup-Events. `static:true` deaktiviert Drag UND Resize per Item garantiert.
  const visibleLayout: LayoutItem[] = layout
    .filter((l) => widgetMap[l.i] != null || l.i.startsWith('custom.section.'))
    .map((l) => (editing ? l : { ...l, static: true }));

  return (
    <div className="relative">
      {error && (
        <div className="mb-2 p-2 rounded bg-red-500/10 border border-red-500/30 text-xs text-red-300">
          Layout-Speicherung: {error}
        </div>
      )}
      {saving && (
        <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-muted-foreground bg-card/90 px-2 py-1 rounded shadow z-10">
          <Loader2 size={12} className="animate-spin" /> speichere…
        </div>
      )}
      {/* Widget-hinzufuegen-Button — nur im pageId-Edit-Mode (Legacy-pageSlug ist read-only). */}
      {editing && (pageId || templateId) && (
        <div className="mb-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={addCustomSection}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm min-h-[40px] bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700"
            title="Eigene Trennlinie mit Ueberschrift hinzufuegen"
          >
            <SeparatorHorizontal size={14} /> Trennlinie
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm min-h-[40px] transition-colors ${
              pickerOpen
                ? 'bg-brand-600/30 text-brand-200 border border-brand-600/50'
                : 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700'
            }`}
            title="Widgets zur Seite hinzufuegen oder entfernen"
          >
            <Plus size={14} /> Widgets verwalten
          </button>
        </div>
      )}
      <div ref={containerRef}>
      <GridLayout
        className="layout"
        layout={visibleLayout}
        width={width}
        // 2026-05-29: react-grid-layout v2 hat die Grid-Metriken in ein
        // gridConfig-Objekt gebuendelt — direkte `cols`/`rowHeight`/`margin`
        // Props werden seit v2.0 IGNORIERT. Vorher liefen wir mit den
        // Library-Defaults (rowHeight=150, margin=[10,10]), was die fette
        // Section-Lücke verursachte. rowHeight=35 ergibt einen 35-px Section-
        // Slot, der Header (h-7 = 28 px) sitzt eng am naechsten Widget.
        // Alle default_h in widget-registry.ts wurden dafuer x2.
        gridConfig={{ cols: 12, rowHeight: 35, margin: [16, 6] as [number, number], containerPadding: null, maxRows: Infinity }}
        isDraggable={editing}
        isResizable={editing}
        draggableHandle=".widget-drag-handle"
        compactType="vertical"
        preventCollision={false}
        onLayoutChange={(newLayout: ReadonlyArray<LayoutItem>) => {
          if (!editing) return;
          // react-grid-layout liefert nur die geometrischen Felder zurueck (i/x/y/w/h/minW/minH).
          // Eigene Properties wie `config` (custom.section-Label) wuerden sonst beim
          // ersten Drag verloren gehen — daher hier per Slug aus dem alten Layout mergen.
          setLayout((prev) => {
            const prevBySlug = new Map((prev ?? []).map((l) => [l.i, l]));
            return newLayout.map((l) => {
              const old = prevBySlug.get(l.i);
              return old?.config ? { ...l, config: old.config } : l;
            });
          });
        }}
      >
        {visibleLayout.map((l) => {
          const slug = l.i;
          // Section-Header-Widgets rendern ohne Card-Border. Zwei Varianten:
          //   1. `*.section-*`  — hardcoded vom Vorlagen-Layout (Default-Label in config.label
          //      seit 2026-05-28, sonst Fallback aus widgetMap fuer alte Layouts).
          //   2. `custom.section.*` — user-angelegt mit config.label (Run-3 #7).
          // Joerg 2026-05-28: kompakt rendern — Trennlinie nur in der oberen
          // Haelfte des Grid-Slots (h-9 ≈ 36 px), untere Haelfte dient als
          // Abstand zum naechsten Block. Stift, Drag-Handle und X sind
          // inline links/rechts neben dem h2-Text statt absolute.
          const isHardcodedSection = /\.section-/.test(slug) && !slug.startsWith('custom.');
          const isCustomSection = slug.startsWith('custom.section.');
          const isSection = isHardcodedSection || isCustomSection;
          const configLabel = typeof l.config?.['label'] === 'string' ? (l.config['label'] as string) : null;
          const displayLabel = configLabel ?? (isCustomSection ? 'Neue Sektion' : null);

          if (isSection) {
            return (
              <div key={slug} className="relative">
                <div className="flex items-center gap-1 px-2 h-7">
                  {editing && (pageId || templateId) && (
                    <>
                      <span
                        className="widget-drag-handle cursor-move text-brand-300/70 hover:text-brand-300 p-1 -m-1 touch-none"
                        title="Ziehen zum Sortieren"
                      >
                        <GripVertical size={12} />
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); renameCustomSection(slug, configLabel ?? 'Neue Sektion'); }}
                        title="Ueberschrift bearbeiten"
                        aria-label="Ueberschrift bearbeiten"
                        className="text-slate-300 hover:text-slate-100 p-1 -m-1"
                      >
                        <Pencil size={12} />
                      </button>
                    </>
                  )}
                  {displayLabel ? (
                    <h2 className="text-xs uppercase tracking-wide text-muted-foreground border-b border-default flex-1 pb-1 truncate">
                      {displayLabel}
                    </h2>
                  ) : (
                    // Sehr alte Layouts ohne config.label fallen auf widgetMap zurueck
                    <div className="flex-1">{widgetMap[slug]?.(l.config)}</div>
                  )}
                  {editing && (pageId || templateId) && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeWidget(slug); }}
                      title="Trennlinie entfernen"
                      aria-label="Trennlinie entfernen"
                      className="text-red-300 hover:text-red-100 p-1 -m-1"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            );
          }

          const baseCls = `bg-card border border-default rounded-xl overflow-hidden relative ${editing ? 'ring-2 ring-brand-500/40' : ''}`;
          // Admin sieht die Widget-ID:
          //  - im Edit-Modus als Pille rechts in der Drag-Handle-Leiste (kein Overlay)
          //  - sonst als Hover-Tooltip auf der Card (kein Overlay über dem Inhalt)
          const adminTooltip = isAdmin ? `Widget-ID: ${slug}` : undefined;
          return (
            <div key={slug} className={baseCls} title={!editing ? adminTooltip : undefined}>
              {editing && (
                <>
                  <div className="widget-drag-handle cursor-move bg-brand-500/10 border-b border-brand-500/20 px-2 py-1 text-[10px] text-brand-300 select-none flex items-center justify-between gap-2">
                    <span>⋮⋮ ziehen / Ecke zum Resize</span>
                    {isAdmin && (
                      <span
                        className="font-mono text-[10px] text-brand-200/80 bg-brand-950/40 px-1.5 py-0.5 rounded select-text"
                        title={adminTooltip}
                      >
                        {slug}
                      </span>
                    )}
                  </div>
                  {pageId && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeWidget(slug); }}
                      title="Widget entfernen"
                      aria-label="Widget entfernen"
                      className="absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded bg-red-500/20 text-red-300 hover:bg-red-500/40 hover:text-red-100 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  )}
                </>
              )}
              <div className="h-full overflow-auto">
                <ModuleErrorBoundary label={slug} scope="widget">
                  {widgetMap[slug]?.(l.config)}
                </ModuleErrorBoundary>
              </div>
            </div>
          );
        })}
      </GridLayout>
      </div>
      {/* Widget-Picker-Drawer — Fixed Panel rechts am Viewport, slide-in. */}
      {editing && (pageId || templateId) && pickerOpen && (
        <WidgetPicker
          widgetsByModule={widgetsByModule}
          activeSlugSet={activeSlugSet}
          onAdd={addWidget}
          onRemove={removeWidget}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {source === 'fallback' && (
        <div className="mt-2 text-[11px] text-muted-foreground italic">
          Standard-Anordnung (noch kein eigenes Layout gespeichert).
        </div>
      )}
      {source === 'user' && (
        <div className="mt-2 text-[11px] text-muted-foreground italic">
          Eigene Anordnung aktiv.
        </div>
      )}
    </div>
  );
}

export type { Layout, LayoutItem };

/**
 * Widget-Picker-Drawer (Phase 2d-Folgearbeit 2026-05-27). Fixed Panel rechts
 * am Viewport mit allen erlaubten Widgets, nach Modul gruppiert.
 *
 * Bedienung (Joerg-Wunsch 2026-05-27):
 *   - Einfacher Klick → Widget wird selektiert, Beschreibung erscheint im
 *     festen Detail-Slot unten im Drawer.
 *   - Doppelklick → Widget wird zur Seite hinzugefuegt (nur wenn noch
 *     nicht aktiv), bleibt selektiert.
 *   - „Entfernen"-Inline-Button → entfernt ein aktives Widget.
 */
function WidgetPicker({
  widgetsByModule,
  activeSlugSet,
  onAdd,
  onRemove,
  onClose,
}: {
  widgetsByModule: Array<[string, AvailableWidget[]]>;
  activeSlugSet: Set<string>;
  onAdd: (w: AvailableWidget) => void;
  onRemove: (slug: string) => void;
  onClose: () => void;
}) {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // 2026-05-29 (Joerg): Suchfeld filtert Label + Beschreibung +
  // Modul-Name. Module ohne Treffer fallen weg.
  const filteredByModule = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return widgetsByModule;
    const result: Array<[string, AvailableWidget[]]> = [];
    for (const [moduleName, widgets] of widgetsByModule) {
      const hits = widgets.filter((w) =>
        w.label.toLowerCase().includes(term) ||
        w.description.toLowerCase().includes(term) ||
        moduleName.toLowerCase().includes(term) ||
        w.slug.toLowerCase().includes(term),
      );
      if (hits.length > 0) result.push([moduleName, hits]);
    }
    return result;
  }, [widgetsByModule, search]);

  const selected = useMemo(() => {
    if (!selectedSlug) return null;
    for (const [, widgets] of widgetsByModule) {
      const hit = widgets.find((w) => w.slug === selectedSlug);
      if (hit) return hit;
    }
    return null;
  }, [selectedSlug, widgetsByModule]);

  return (
    <div className="fixed top-0 right-0 bottom-0 w-96 max-w-[90vw] z-50 bg-card border-l border-default shadow-2xl flex flex-col">
      <header className="px-4 py-3 border-b border-default flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Widgets verwalten</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schliessen"
          title="Schliessen"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X size={18} />
        </button>
      </header>

      <div className="px-4 py-2 text-[11px] text-muted-foreground bg-background/40 border-b border-default">
        Einfacher Klick zeigt die Beschreibung, Doppelklick fuegt das Widget zur Seite hinzu.
      </div>

      <div className="px-3 py-2 border-b border-default bg-background/30">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Widget suchen — Name, Beschreibung, Modul, ID…"
          className="w-full px-3 py-1.5 rounded-md bg-background border border-default text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {filteredByModule.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            {search.trim() ? `Keine Treffer für „${search.trim()}".` : 'Keine Widgets verfuegbar.'}
          </div>
        ) : (
          filteredByModule.map(([module, widgets]) => (
            <section key={module} className="border-b border-default/60 last:border-b-0">
              <div className="px-4 py-2 text-[11px] uppercase tracking-wide text-muted-foreground bg-background/50">
                {module}
              </div>
              <ul>
                {widgets.map((w) => {
                  const active = activeSlugSet.has(w.slug);
                  const isSelected = selectedSlug === w.slug;
                  return (
                    <li
                      key={w.slug}
                      onClick={() => setSelectedSlug(w.slug)}
                      onDoubleClick={() => { setSelectedSlug(w.slug); if (!active) onAdd(w); }}
                      className={
                        'px-4 py-2 flex items-center justify-between gap-2 cursor-pointer select-none transition-colors ' +
                        (isSelected
                          ? 'bg-brand-500/10 ring-1 ring-inset ring-brand-500/40'
                          : 'hover:bg-background/40')
                      }
                      title={active ? 'Schon auf der Seite — Doppelklick ohne Wirkung' : 'Doppelklick fuegt zur Seite hinzu'}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={'text-sm truncate ' + (active ? 'text-foreground/70' : 'text-foreground')}>
                          {w.label}
                          {active && <span className="ml-1 text-[10px] text-brand-400">• aktiv</span>}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground/80 truncate" title="Widget-ID (eindeutig, kommt auch auf der Seite vor)">
                          {w.slug}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {w.default_w} × {w.default_h}
                        </div>
                      </div>
                      {active && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onRemove(w.slug); }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                          title="Widget von der Seite entfernen"
                        >
                          <X size={12} /> Entfernen
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>

      {/* Detail-Slot: Beschreibung des aktuell selektierten Widgets. */}
      <div className="border-t border-default bg-background/40 px-4 py-3 max-h-48 overflow-y-auto">
        {selected ? (
          <>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-sm font-medium text-foreground truncate">{selected.label}</div>
              {activeSlugSet.has(selected.slug) ? (
                <button
                  type="button"
                  onClick={() => onRemove(selected.slug)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                  title="Widget von der Seite entfernen"
                >
                  <X size={12} /> Entfernen
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onAdd(selected)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-brand-600/20 text-brand-200 hover:bg-brand-600/40 transition-colors"
                  title="Widget zur Seite hinzufuegen"
                >
                  <Plus size={12} /> Hinzufuegen
                </button>
              )}
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
              {selected.description || 'Keine Beschreibung hinterlegt.'}
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground italic">
            Klicke ein Widget an, um seine Beschreibung zu sehen.
          </div>
        )}
      </div>

      <footer className="px-4 py-2 border-t border-default text-[11px] text-muted-foreground">
        Aenderungen werden mit „Speichern" im Seiten-Header uebernommen.
      </footer>
    </div>
  );
}
